import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Text,
  VStack,
  HStack,
  Flex,
  Tag,
  TagLabel,
  IconButton,
  Spinner,
  Card,
  CardHeader,
  CardBody,
  Heading,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure
} from '@chakra-ui/react';
import { ViewIcon } from '@chakra-ui/icons';
import { callApi } from '../../callApi';

const VarianceDocumentViewer = ({ caseId, caseFiles, accessToken }) => {
  const [selectedFileId, setSelectedFileId] = useState('');
  const [docUrl, setDocUrl] = useState(null);
  const [isDocLoading, setIsDocLoading] = useState(false);
  const [error, setError] = useState(null);

  const { isOpen: isTextModalOpen, onOpen: onTextModalOpen, onClose: onTextModalClose } = useDisclosure();
  const [selectedTextFile, setSelectedTextFile] = useState(null);

  const { isOpen: isPdfModalOpen, onOpen: onPdfModalOpen, onClose: onPdfModalClose } = useDisclosure();
  const [selectedPdfFile, setSelectedPdfFile] = useState(null);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getDisplayType = (file) => {
    if (file?.document_metadata?.ai_triage_category) {
      return file.document_metadata.ai_triage_category;
    }
    return file?.file_type_description || file?.file_type || 'Unknown';
  };

  // Helper to determine category if not explicitly set
  const getCategory = (file) => {
    if (file.document_category) return file.document_category;
    
    const type = (file.file_type_description || file.file_type || '').toLowerCase();
    if (type.includes('cms') || type.includes('claim') || type.includes('eob') || type.includes('denial') || type.includes('remittance') || type.includes('1500')) {
      return 'BillingAndClaims';
    }
    if (type.includes('op note') || type.includes('clinical') || type.includes('medical') || type.includes('report')) {
      return 'ClinicalDocumentation';
    }
    return 'Other';
  };

  const handleFileSelect = async (file) => {
    setSelectedFileId(file.id);
    setSelectedPdfFile(file);
    onPdfModalOpen();
    
    if (!file.id) {
      if (docUrl) window.URL.revokeObjectURL(docUrl);
      setDocUrl(null);
      return;
    }

    setIsDocLoading(true);
    if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
    }
    setDocUrl(null);

    try {
        const response = await callApi('/download_case_file', accessToken, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                case_id: caseId,
                file_id: file.id,
                disposition: 'inline'
            }),
        });

        if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setDocUrl(url);
        } else {
            throw new Error('Failed to load file');
        }
    } catch (error) {
        console.error('Error loading file:', error);
        setError('Failed to load file for viewing.');
    } finally {
        setIsDocLoading(false);
    }
  };

  const handlePdfModalClose = () => {
    onPdfModalClose();
    if (docUrl) {
      window.URL.revokeObjectURL(docUrl);
      setDocUrl(null);
    }
    setSelectedFileId('');
    setSelectedPdfFile(null);
  };

  useEffect(() => {
    return () => {
      if (docUrl) {
        window.URL.revokeObjectURL(docUrl);
      }
    };
  }, [docUrl]);

  
  return (
    <Box mt={6}>
      <Card bg="white" borderColor="gray.200">
        <CardHeader p={4} bg="gray.50" borderBottomWidth="1px">
          <Heading size="sm">Source Documents</Heading>
        </CardHeader>
        <CardBody p={4}>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
            {/* Clinical Documentation */}
            <Box>
              <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3} borderBottomWidth="1px" pb={1}>
                Clinical Documentation
              </Text>
              <VStack align="stretch" spacing={2}>
                {caseFiles.filter(f => getCategory(f) === 'ClinicalDocumentation').length > 0 ? (
                  caseFiles
                    .filter(f => getCategory(f) === 'ClinicalDocumentation')
                    .sort((a, b) => getDisplayType(a).localeCompare(getDisplayType(b)))
                    .map(file => (
                    <Box
                      key={file.id}
                      p={3}
                      cursor="pointer"
                      borderRadius="md"
                      bg="white"
                      _hover={{ bg: "blue.50", borderColor: "blue.200" }}
                      borderWidth="1px"
                      borderColor="gray.200"
                      onClick={() => handleFileSelect(file)}
                      transition="all 0.2s"
                    >
                      <Flex justify="space-between" align="center">
                        <VStack align="start" spacing={1} flex="1">
                          <Text fontSize="sm" fontWeight="medium" noOfLines={1} title={file.file_name}>
                            {file.file_name}
                          </Text>
                          <HStack>
                            <Tag size="sm" colorScheme="blue" variant="subtle">
                              <TagLabel fontSize="xs">{getDisplayType(file)}</TagLabel>
                            </Tag>
                            <Text fontSize="xs" color="gray.500">
                              {formatFileSize(file.file_size)}
                            </Text>
                          </HStack>
                        </VStack>
                        <IconButton
                          icon={<ViewIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          aria-label="View file text"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTextFile(file);
                            onTextModalOpen();
                          }}
                        />
                      </Flex>
                    </Box>
                  ))
                ) : (
                  <Text fontSize="sm" color="gray.500" fontStyle="italic">No clinical documents found.</Text>
                )}
              </VStack>
            </Box>

            {/* Right Column: Billing & Claims + Other Documents */}
            <VStack align="stretch" spacing={6}>
              {/* Billing & Claims */}
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3} borderBottomWidth="1px" pb={1}>
                  Billing & Claims
                </Text>
                <VStack align="stretch" spacing={2}>
                  {caseFiles.filter(f => getCategory(f) === 'BillingAndClaims').length > 0 ? (
                    caseFiles
                      .filter(f => getCategory(f) === 'BillingAndClaims')
                      .sort((a, b) => getDisplayType(a).localeCompare(getDisplayType(b)))
                      .map(file => (
                      <Box
                        key={file.id}
                        p={3}
                        cursor="pointer"
                        borderRadius="md"
                        bg="white"
                        _hover={{ bg: "green.50", borderColor: "green.200" }}
                        borderWidth="1px"
                        borderColor="gray.200"
                        onClick={() => handleFileSelect(file)}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1} title={file.file_name}>
                              {file.file_name}
                            </Text>
                            <HStack>
                              <Tag size="sm" colorScheme="green" variant="subtle">
                                <TagLabel fontSize="xs">{getDisplayType(file)}</TagLabel>
                              </Tag>
                              <Text fontSize="xs" color="gray.500">
                                {formatFileSize(file.file_size)}
                              </Text>
                            </HStack>
                          </VStack>
                          <IconButton
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="View file text"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTextFile(file);
                              onTextModalOpen();
                            }}
                          />
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">No billing documents found.</Text>
                  )}
                </VStack>
              </Box>

              {/* Other Documents */}
              <Box>
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={3} borderBottomWidth="1px" pb={1}>
                  Other Documents
                </Text>
                <VStack align="stretch" spacing={2}>
                  {caseFiles.filter(f => getCategory(f) !== 'ClinicalDocumentation' && getCategory(f) !== 'BillingAndClaims').length > 0 ? (
                    caseFiles
                      .filter(f => getCategory(f) !== 'ClinicalDocumentation' && getCategory(f) !== 'BillingAndClaims')
                      .sort((a, b) => getDisplayType(a).localeCompare(getDisplayType(b)))
                      .map(file => (
                      <Box
                        key={file.id}
                        p={3}
                        cursor="pointer"
                        borderRadius="md"
                        bg="white"
                        _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                        borderWidth="1px"
                        borderColor="gray.200"
                        onClick={() => handleFileSelect(file)}
                        transition="all 0.2s"
                      >
                        <Flex justify="space-between" align="center">
                          <VStack align="start" spacing={1} flex="1">
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1} title={file.file_name}>
                              {file.file_name}
                            </Text>
                            <HStack>
                              <Tag size="sm" colorScheme="gray" variant="subtle">
                                <TagLabel fontSize="xs">{getDisplayType(file)}</TagLabel>
                              </Tag>
                              <Text fontSize="xs" color="gray.500">
                                {formatFileSize(file.file_size)}
                              </Text>
                            </HStack>
                          </VStack>
                          <IconButton
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            aria-label="View file text"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTextFile(file);
                              onTextModalOpen();
                            }}
                          />
                        </Flex>
                      </Box>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.500" fontStyle="italic">No other documents found.</Text>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Grid>
        </CardBody>
      </Card>

      {/* PDF Viewer Modal */}
      <Modal isOpen={isPdfModalOpen} onClose={handlePdfModalClose} size="6xl">
        <ModalOverlay />
        <ModalContent h="90vh">
          <ModalHeader pb={2}>
            {selectedPdfFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={3} mt={-1}>
              <TagLabel>{getDisplayType(selectedPdfFile)}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody p={0} bg="gray.100">
            {isDocLoading ? (
              <Flex justify="center" align="center" h="100%">
                <VStack>
                  <Spinner size="xl" color="blue.500" />
                  <Text>Loading Document...</Text>
                </VStack>
              </Flex>
            ) : docUrl ? (
              <iframe 
                src={docUrl} 
                title="Document Viewer"
                width="100%" 
                height="100%" 
                style={{ border: 'none' }} 
              />
            ) : (
              <Flex justify="center" align="center" h="100%" color="gray.500">
                <Text>Failed to load document.</Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* File Content Modal */}

      <Modal isOpen={isTextModalOpen} onClose={onTextModalClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            {selectedTextFile?.file_name}
            <Tag size="sm" colorScheme="blue" ml={2} mt={-1}>
              <TagLabel>{getDisplayType(selectedTextFile)}</TagLabel>
            </Tag>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box 
              p={4} 
              borderWidth="1px" 
              borderRadius="md" 
              bg="gray.50" 
              maxH="500px" 
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontFamily="monospace"
            >
              {selectedTextFile?.file_text || "No content available"}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Text fontSize="sm" color="gray.500" mr="auto">
              {selectedTextFile ? formatFileSize(selectedTextFile.file_size) : ""} • 
              {selectedTextFile ? " Uploaded " + (selectedTextFile.upload_date ? new Date(selectedTextFile.upload_date).toLocaleDateString() : "Unknown") : ""}
            </Text>
            <Button colorScheme="blue" mr={3} onClick={onTextModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VarianceDocumentViewer;
